/* MealComposerCard — iter423 AI Meal Composer with buffering analysis.
 *
 * Потребителят избира N храни (от любими + търсене + suggestion pool).
 * Системата:
 *   • Изчислява NIS с buffering (animal/plant fat × protein modifiers, Holt 1997)
 *   • Анализира фибро-буфериране (carb→fiber ratio)
 *   • AI commentary за взаимодействията
 *   • Swap предложения за по-ниско NIS
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles, Plus, X, Search, Loader2, Heart, ChefHat,
  Flame, Activity, Zap, Lightbulb, ArrowRight, Info,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const MEAL_LABELS = {
  breakfast: { bg: "Закуска", en: "Breakfast" },
  lunch:     { bg: "Обяд",    en: "Lunch" },
  dinner:    { bg: "Вечеря",  en: "Dinner" },
  snack:     { bg: "Снак",    en: "Snack" },
};

export default function MealComposerCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [mealKey, setMealKey] = useState("lunch");
  const [items, setItems] = useState([]);  // [{name, grams}]
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [composing, setComposing] = useState(false);
  const [result, setResult] = useState(null);

  const fetchFavorites = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/health-intelligence/snapshots?days=90&type=favorite_food&limit=30`);
      const names = [];
      for (const it of (r.data?.items || [])) {
        const fn = it.payload?.food_name;
        if (!fn) continue;
        const bgN = fn.includes(" / ") ? fn.split(" / ").slice(-1)[0].trim() : fn;
        const cleaned = bgN.replace(/\(.*?\)/g, "").replace(/[,]+\s*$/, "").trim();
        if (cleaned && cleaned.length >= 2 && cleaned.length <= 50) names.push(cleaned);
      }
      const seen = new Set(); const uniq = [];
      for (const n of names) { const k = n.toLowerCase(); if (!seen.has(k)) { seen.add(k); uniq.push(n); } }
      setFavorites(uniq);
    } catch (_e) {}
  }, [API]);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  // debounced search
  useEffect(() => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const h = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/food/search?q=${encodeURIComponent(query)}&limit=6`);
        setSearchResults(r.data?.products || []);
      } catch (_e) { setSearchResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(h);
  }, [query, API]);

  const addItem = (name) => {
    if (!name || items.some((it) => it.name.toLowerCase() === name.toLowerCase())) return;
    setItems((arr) => [...arr, { name, grams: 100 }]);
    setQuery("");
    setSearchResults([]);
  };

  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const updateGrams = (idx, g) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, grams: Math.max(1, Number(g) || 1) } : it)));

  const handleCompose = async () => {
    if (items.length === 0) return;
    setComposing(true);
    try {
      const r = await axios.post(`${API}/health-intelligence/personalized-menu/compose-meal`, {
        meal_key: mealKey,
        foods: items,
        use_ai_commentary: true,
      });
      setResult(r.data);
      toast.success(bg ? "Анализът е готов" : "Analysis ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally { setComposing(false); }
  };

  const totals = result?.totals || {};
  const buffering = result?.buffering || {};

  return (
    <Card className={`border ${isDark ? "border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-fuchsia-500/5" : "border-violet-200 bg-gradient-to-br from-violet-50/40 to-fuchsia-50/30"}`}
          data-testid="meal-composer-card">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">
            <ChefHat className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Композирай ястие — AI анализ на буферирането" : "Compose meal — AI buffering analysis"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? "Изберете храни → AI смесва, анализира животински/растителни мазнини+протеини и буферирането на фибрите."
                : "Pick foods → AI mixes, analyses animal/plant fat+protein and fiber buffering."}
            </p>
          </div>
        </div>

        {/* Meal-key selector */}
        <div className="flex gap-1.5">
          {Object.keys(MEAL_LABELS).map((mk) => (
            <button key={mk} onClick={() => setMealKey(mk)}
                    data-testid={`composer-meal-${mk}`}
                    className={`flex-1 text-[11px] py-1.5 rounded-lg font-medium transition-colors ${
                      mealKey === mk
                        ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
                        : (isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-white text-slate-700 hover:bg-slate-50 border border-slate-200")
                    }`}>
              {bg ? MEAL_LABELS[mk].bg : MEAL_LABELS[mk].en}
            </button>
          ))}
        </div>

        {/* Favorites quick-pick */}
        {favorites.length > 0 && (
          <div className="space-y-1">
            <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
              <Heart className="w-3 h-3" /> {bg ? "От Вашите любими" : "From favorites"}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {favorites.slice(0, 10).map((f, i) => (
                <button key={i} onClick={() => addItem(f)}
                        data-testid={`composer-fav-${i}`}
                        className={`text-[11px] px-2 py-1 rounded-full transition-colors ${
                          items.some((it) => it.name === f)
                            ? (isDark ? "bg-emerald-500 text-white opacity-50" : "bg-emerald-500 text-white opacity-50")
                            : (isDark ? "bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25" : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100")
                        }`}>
                  + {f}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="space-y-1">
          <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            <Search className="w-3 h-3" /> {bg ? "Или потърси" : "Or search"}
          </p>
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder={bg ? "напр. сьомга, киноа, авокадо…" : "e.g. salmon, quinoa, avocado…"}
                 data-testid="composer-search-input"
                 className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`} />
          {searching && <p className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}><Loader2 className="inline w-3 h-3 mr-1 animate-spin" />{bg ? "Търся…" : "Searching…"}</p>}
          {searchResults.length > 0 && (
            <div className={`max-h-32 overflow-y-auto rounded-lg border ${isDark ? "border-slate-700" : "border-slate-200"}`}>
              {searchResults.map((r, i) => {
                const label = r.name_bg || r.name || "—";
                return (
                  <button key={i} onClick={() => addItem(label)}
                          data-testid={`composer-search-${i}`}
                          className={`w-full text-left px-2 py-1.5 text-[11px] border-b last:border-b-0 transition-colors ${
                            isDark ? "border-slate-800 hover:bg-slate-800 text-white" : "border-slate-100 hover:bg-slate-50 text-slate-900"
                          }`}>
                    + {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected items list */}
        {items.length > 0 && (
          <div className={`p-2.5 rounded-xl border ${isDark ? "border-violet-500/30 bg-violet-500/5" : "border-violet-200 bg-violet-50/40"}`} data-testid="composer-items">
            <p className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
              {bg ? `Избрани (${items.length})` : `Selected (${items.length})`}
            </p>
            <div className="space-y-1">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className={`text-xs flex-1 truncate ${isDark ? "text-white" : "text-slate-900"}`}>{it.name}</span>
                  <Input type="number" value={it.grams} min={1} max={2000}
                         onChange={(e) => updateGrams(i, e.target.value)}
                         data-testid={`composer-grams-${i}`}
                         className={`w-16 h-6 text-[11px] ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white text-slate-900 border-slate-300"}`} />
                  <span className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>g</span>
                  <button onClick={() => removeItem(i)}
                          data-testid={`composer-remove-${i}`}
                          className={`w-5 h-5 rounded flex items-center justify-center ${isDark ? "hover:bg-rose-500/20 text-rose-300" : "hover:bg-rose-100 text-rose-600"}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <Button onClick={handleCompose} disabled={composing}
                    className="w-full mt-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white"
                    data-testid="composer-analyze-btn">
              {composing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              {bg ? "Анализирай ястието" : "Analyse meal"}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-2.5" data-testid="composer-result">
            {/* Verdict */}
            <div className={`p-2.5 rounded-xl ${
              (totals.nis_pct || 0) < 35 ? (isDark ? "bg-emerald-500/15 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200")
              : (totals.nis_pct || 0) < 50 ? (isDark ? "bg-amber-500/15 border border-amber-500/30" : "bg-amber-50 border border-amber-200")
              : (isDark ? "bg-rose-500/15 border border-rose-500/30" : "bg-rose-50 border border-rose-200")
            }`}>
              <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{result.verdict_bg}</p>
            </div>

            {/* Totals — 4 tiles */}
            <div className="grid grid-cols-4 gap-1.5">
              <div className={`p-2 rounded-lg text-center ${isDark ? "bg-rose-500/15" : "bg-rose-50"}`}>
                <Flame className={`w-3 h-3 mx-auto ${isDark ? "text-rose-300" : "text-rose-600"}`} />
                <p className={`text-[9px] uppercase mt-0.5 font-bold ${isDark ? "text-rose-300" : "text-rose-700"}`}>kcal</p>
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{Math.round(totals.kcal || 0)}</p>
              </div>
              <div className={`p-2 rounded-lg text-center ${isDark ? "bg-orange-500/15" : "bg-orange-50"}`}>
                <p className={`text-[9px] uppercase font-bold mt-0.5 ${isDark ? "text-orange-300" : "text-orange-700"}`}>DIL</p>
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{totals.dil_g}g</p>
              </div>
              <div className={`p-2 rounded-lg text-center ${isDark ? "bg-violet-500/15" : "bg-violet-50"}`}>
                <Activity className={`w-3 h-3 mx-auto ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                <p className={`text-[9px] uppercase font-bold mt-0.5 ${isDark ? "text-violet-300" : "text-violet-700"}`}>NIS</p>
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{totals.nis_pct}%</p>
              </div>
              <div className={`p-2 rounded-lg text-center ${isDark ? "bg-amber-500/15" : "bg-amber-50"}`}>
                <Zap className={`w-3 h-3 mx-auto ${isDark ? "text-amber-300" : "text-amber-600"}`} />
                <p className={`text-[9px] uppercase font-bold mt-0.5 ${isDark ? "text-amber-300" : "text-amber-700"}`}>UIS</p>
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{totals.uis_pct}%</p>
              </div>
            </div>

            {/* NIS breakdown */}
            {totals.nis_breakdown && (
              <div className={`p-2 rounded-lg text-[10.5px] ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}>
                <span className={isDark ? "text-slate-400" : "text-slate-600"}>{bg ? "Класификация:" : "Classification:"}</span>{" "}
                <span className={isDark ? "text-violet-200" : "text-violet-800"}>
                  {bg ? "мазнини" : "fat"}: {totals.nis_breakdown.fat_type} ·{" "}
                  {bg ? "протеин" : "protein"}: {totals.nis_breakdown.protein_type}
                </span>
                {totals.nis_breakdown.pure_fat_guard_active && (
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-blue-500/20 text-blue-200" : "bg-blue-50 text-blue-700"}`}>
                    {bg ? "💧 Pure-fat guard активен" : "💧 Pure-fat guard"}
                  </span>
                )}
              </div>
            )}

            {/* Buffering analysis */}
            <div className={`p-2.5 rounded-xl border ${isDark ? "border-emerald-500/20 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/40"}`}
                 data-testid="composer-buffering">
              <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1.5 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                <Info className="w-3 h-3" /> {bg ? "Буфериращ анализ" : "Buffering analysis"}
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10.5px] mb-1.5">
                <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                  <span className="opacity-70">{bg ? "Net carbs:" : "Net carbs:"}</span> <strong>{buffering.net_carbs_g}g</strong>
                </div>
                <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                  <span className="opacity-70">{bg ? "Фибри:" : "Fiber:"}</span> <strong>{buffering.fiber_g}g</strong>
                  <span className="opacity-60"> ({Math.round((buffering.fiber_to_carb_ratio || 0) * 100)}%)</span>
                </div>
                <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                  <span className="opacity-70">🥩 {bg ? "Жив. протеин:" : "Animal protein:"}</span> <strong>{buffering.animal_protein_g}g</strong>
                </div>
                <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                  <span className="opacity-70">🌱 {bg ? "Раст. протеин:" : "Plant protein:"}</span> <strong>{buffering.plant_protein_g}g</strong>
                </div>
                <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                  <span className="opacity-70">🧈 {bg ? "Жив. мазнина:" : "Animal fat:"}</span> <strong>{buffering.animal_fat_g}g</strong>
                </div>
                <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                  <span className="opacity-70">🫒 {bg ? "Раст. мазнина:" : "Plant fat:"}</span> <strong>{buffering.plant_fat_g}g</strong>
                </div>
              </div>
              {Array.isArray(buffering.buffer_notes_bg) && buffering.buffer_notes_bg.map((n, i) => (
                <p key={i} className={`text-[10.5px] leading-relaxed mt-1 ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
                  • {n}
                </p>
              ))}
            </div>

            {/* AI commentary */}
            {result.ai_commentary_bg && (
              <div className={`p-2.5 rounded-xl border-l-4 ${isDark ? "border-l-violet-400 bg-violet-500/5" : "border-l-violet-500 bg-violet-50/40"}`}
                   data-testid="composer-ai-commentary">
                <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                  <Sparkles className="w-3 h-3" /> {bg ? "NutriBot коментар" : "NutriBot commentary"}
                </p>
                <p className={`text-[11px] leading-relaxed ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                  {result.ai_commentary_bg}
                </p>
              </div>
            )}

            {/* Swap suggestions */}
            {Array.isArray(result.swap_suggestions) && result.swap_suggestions.length > 0 && (
              <div className={`p-2.5 rounded-xl border ${isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50/40"}`}
                   data-testid="composer-swaps">
                <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  <Lightbulb className="w-3 h-3" /> {bg ? "Предложения за подобрение" : "Swap suggestions"}
                </p>
                <div className="space-y-1">
                  {result.swap_suggestions.map((s, i) => (
                    <div key={i} className={`text-[10.5px] leading-relaxed ${isDark ? "text-amber-100" : "text-amber-900"}`}>
                      <span className="font-bold">{s.from}</span>
                      <ArrowRight className="inline w-3 h-3 mx-1 opacity-60" />
                      <span className="font-bold">{s.to}</span>
                      <span className={`block italic text-[10px] ${isDark ? "text-amber-200/80" : "text-amber-700/80"}`}>{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
